import * as actionTypes from '../actions/actionTypes';

import {
    IMySavedItemsList,
    IPaymentMethodsList,
    IAddressesList,
    IPhysicalStore,
    IUserSettings,
    PersonalDetails,
    PersonalDetailsGeographicInformations,
    PersonalInformation
} from '@interfaces/myProfileInterfaces';
import { RequestStatus } from '@interfaces/mainInterfaces';

import { updateObject } from '@utils/utils';

class InitialStateProps {
    //PERSONAL DETAILS
    personalDetails: PersonalInformation = null;
    personalDetailsRequestStatus: RequestStatus = 'IDLE';

    getGeographicInformationsRequestStatus: RequestStatus = 'IDLE';
    geographicInformations: PersonalDetailsGeographicInformations = null;

    updatePersonalDetailsRequestStatus: RequestStatus = 'IDLE';

    //SETTINGS
    updateSettingsPasswordRequestStatus: RequestStatus = 'IDLE';

    errorMessage: string = null;

    userSettings: IUserSettings = null;
    getUserSettingsRequestStatus: RequestStatus = 'IDLE';

    updatePersonalizationSettingsRequestStatus: RequestStatus = 'IDLE';

    createSubscriptionRequestStatus: RequestStatus = 'IDLE';

    deleteSubscriptionRequestStatus: RequestStatus = 'IDLE';

    //MY STORES
    favouritesStores: IPhysicalStore[] = null;
    getFavouritesStoresRequestStatus: RequestStatus = 'IDLE';
    removeFavouriteStoreRequestStatus: RequestStatus = 'IDLE';
    setDefaultStoreRequestStatus: RequestStatus = 'IDLE';

    //PAYMENT METHODS
    addPaymentMethodRequestStatus: RequestStatus = 'IDLE';
    editPaymentMethodRequestStatus: RequestStatus = 'IDLE';

    paymentMethodsList: IPaymentMethodsList = null;
    getPaymentMethodsListRequestStatus: RequestStatus = 'IDLE';

    setDefaultPaymentMethodsStatus: RequestStatus = 'IDLE';

    removePaymentMethodRequestStatus: RequestStatus = 'IDLE';

    // ADDRESSES
    addressesList: IAddressesList = null;
    getAddressesListRequestStatus: RequestStatus = 'IDLE';

    //MY SAVED ITEMS
    getMySavedItemsRequestStatus: RequestStatus = 'IDLE';
    mySavedItems: IMySavedItemsList = null;

    //SUBSCRIPTION
    userSubscriptionEmail: boolean = null;
    userSubscriptionPhone: boolean = null;
    userSubscriptionEmailChanged: boolean = false;
    userSubscriptionPhoneChanged: boolean = false;
    userSubscriptionCorrect: boolean = null;
}

const initialState: InitialStateProps = new InitialStateProps();

const myProfileReducer = (state = initialState, action): InitialStateProps => {
    switch (action.type) {
        case actionTypes.GET_MY_PROFILE_PERSONAL_DETAILS_PENDING:
            return updateObject(state, {
                personalDetailsRequestStatus: 'LOADING',
            });
        case actionTypes.GET_MY_PROFILE_PERSONAL_DETAILS_SUCCESS:
            return updateObject(state, {
                personalDetailsRequestStatus: 'SUCCESS',
                personalDetails: action.personalDetails,
            });
        case actionTypes.GET_MY_PROFILE_PERSONAL_DETAILS_ERROR:
            return updateObject(state, {
                personalDetailsRequestStatus: 'ERROR',
            });
        case actionTypes.GET_GEOGRAPHIC_INFORMATIONS_PENDING:
            return updateObject(state, {
                getGeographicInformationsRequestStatus: 'LOADING',
            });
        case actionTypes.GET_GEOGRAPHIC_INFORMATIONS_SUCCESS:
            return updateObject(state, {
                getGeographicInformationsRequestStatus: 'SUCCESS',
                geographicInformations: action.geographicInformations,
            });
        case actionTypes.GET_GEOGRAPHIC_INFORMATIONS_ERROR:
            return updateObject(state, {
                getGeographicInformationsRequestStatus: 'ERROR',
            });
        case actionTypes.UPDATE_MY_PROFILE_PERSONAL_DETAILS_PENDING:
            return updateObject(state, {
                updatePersonalDetailsRequestStatus: 'LOADING',
            });
        case actionTypes.UPDATE_MY_PROFILE_PERSONAL_DETAILS_SUCCESS:
            return updateObject(state, {
                updatePersonalDetailsRequestStatus: 'SUCCESS',
            });
        case actionTypes.UPDATE_MY_PROFILE_PERSONAL_DETAILS_ERROR:
            return updateObject(state, {
                updatePersonalDetailsRequestStatus: 'ERROR',
            });
        case actionTypes.UPDATE_SETTINGS_PASSWORD_PENDING:
            return updateObject(state, {
                updateSettingsPasswordRequestStatus: 'LOADING',
            });
        case actionTypes.UPDATE_SETTINGS_PASSWORD_SUCCESS:
            return updateObject(state, {
                updateSettingsPasswordRequestStatus: 'SUCCESS',
            });
        case actionTypes.UPDATE_SETTINGS_PASSWORD_ERROR:
            return updateObject(state, {
                updateSettingsPasswordRequestStatus: 'ERROR',
            });
        case actionTypes.UPDATE_SETTINGS_PASSWORD_CUSTOM_ERROR:
            return updateObject(state, {
                updateSettingsPasswordRequestStatus: 'ERROR',
                errorMessage: action.errorMessage,
            });
        case actionTypes.GET_SETTINGS_USER_SETTINGS_PENDING:
            return updateObject(state, {
                getUserSettingsRequestStatus: 'LOADING',
            });
        case actionTypes.GET_SETTINGS_USER_SETTINGS_SUCCESS:
            return updateObject(state, {
                getUserSettingsRequestStatus: 'SUCCESS',
                userSettings: action.userSettings,
            });
        case actionTypes.GET_SETTINGS_USER_SETTINGS_ERROR:
            return updateObject(state, {
                getUserSettingsRequestStatus: 'ERROR',
            });
        case actionTypes.UPDATE_PERSONALIZATION_SETTINGS_PENDING:
            return updateObject(state, {
                updatePersonalizationSettingsRequestStatus: 'LOADING',
            });
        case actionTypes.UPDATE_PERSONALIZATION_SETTINGS_SUCCESS:
            return updateObject(state, {
                updatePersonalizationSettingsRequestStatus: 'SUCCESS',
            });
        case actionTypes.UPDATE_PERSONALIZATION_SETTINGS_ERROR:
            return updateObject(state, {
                updatePersonalizationSettingsRequestStatus: 'ERROR',
            });
        case actionTypes.CREATE_SUBSCRIPTION_PENDING:
            return updateObject(state, {
                createSubscriptionRequestStatus: 'LOADING',
            });
        case actionTypes.CREATE_SUBSCRIPTION_SUCCESS:
            return updateObject(state, {
                createSubscriptionRequestStatus: 'SUCCESS',
            });
        case actionTypes.CREATE_SUBSCRIPTION_ERROR:
            return updateObject(state, {
                createSubscriptionRequestStatus: 'ERROR',
            });
        case actionTypes.DELETE_SUBSCRIPTION_PENDING:
            return updateObject(state, {
                deleteSubscriptionRequestStatus: 'LOADING',
            });
        case actionTypes.DELETE_SUBSCRIPTION_SUCCESS:
            return updateObject(state, {
                deleteSubscriptionRequestStatus: 'SUCCESS',
            });
        case actionTypes.DELETE_SUBSCRIPTION_ERROR:
            return updateObject(state, {
                deleteSubscriptionRequestStatus: 'ERROR',
            });
        case actionTypes.GET_FAVOURITES_STORES_PENDING:
            return updateObject(state, {
                getFavouritesStoresRequestStatus: 'LOADING',
            });
        case actionTypes.GET_FAVOURITES_STORES_SUCCESS:
            return updateObject(state, {
                getFavouritesStoresRequestStatus: 'SUCCESS',
                favouritesStores: action.favouritesStores,
            });
        case actionTypes.GET_FAVOURITES_STORES_ERROR:
            return updateObject(state, {
                getFavouritesStoresRequestStatus: 'ERROR',
            });
        case actionTypes.REMOVE_FAVOURITE_STORE_PENDING:
            return updateObject(state, {
                removeFavouriteStoreRequestStatus: 'LOADING',
            });
        case actionTypes.REMOVE_FAVOURITE_STORE_SUCCESS:
            return updateObject(state, {
                removeFavouriteStoreRequestStatus: 'SUCCESS',
            });
        case actionTypes.REMOVE_FAVOURITE_STORE_ERROR:
            return updateObject(state, {
                removeFavouriteStoreRequestStatus: 'ERROR',
            });
        case actionTypes.SET_DEFAULT_STORE_PENDING:
            return updateObject(state, {
                setDefaultStoreRequestStatus: 'LOADING',
            });
        case actionTypes.SET_DEFAULT_STORE_SUCCESS:
            return updateObject(state, {
                setDefaultStoreRequestStatus: 'SUCCESS',
            });
        case actionTypes.SET_DEFAULT_STORE_ERROR:
            return updateObject(state, {
                setDefaultStoreRequestStatus: 'ERROR',
            });
        case actionTypes.ADD_PAYMENT_METHOD_PENDING:
            return updateObject(state, {
                addPaymentMethodRequestStatus: 'LOADING',
            });
        case actionTypes.ADD_PAYMENT_METHOD_SUCCESS:
            return updateObject(state, {
                addPaymentMethodRequestStatus: 'SUCCESS',
            });
        case actionTypes.ADD_PAYMENT_METHOD_ERROR:
            return updateObject(state, {
                addPaymentMethodRequestStatus: 'ERROR',
            });
        case actionTypes.EDIT_PAYMENT_METHOD_PENDING:
            return updateObject(state, {
                editPaymentMethodRequestStatus: 'LOADING',
            });
        case actionTypes.EDIT_PAYMENT_METHOD_SUCCESS:
            return updateObject(state, {
                editPaymentMethodRequestStatus: 'SUCCESS',
            });
        case actionTypes.EDIT_PAYMENT_METHOD_ERROR:
            return updateObject(state, {
                editPaymentMethodRequestStatus: 'ERROR',
            });
        case actionTypes.GET_PAYMENT_METHODS_PENDING:
            return updateObject(state, {
                getPaymentMethodsListRequestStatus: 'LOADING',
            });
        case actionTypes.GET_PAYMENT_METHODS_SUCCESS:
            return updateObject(state, {
                getPaymentMethodsListRequestStatus: 'SUCCESS',
                paymentMethodsList: action.paymentMethodsList,
            });
        case actionTypes.GET_PAYMENT_METHODS_ERROR:
            return updateObject(state, {
                getPaymentMethodsListRequestStatus: 'ERROR',
            });
        case actionTypes.SET_DEFAULT_PAYMENT_METHOD_PENDING:
            return updateObject(state, {
                setDefaultPaymentMethodsStatus: 'LOADING',
            });
        case actionTypes.SET_DEFAULT_PAYMENT_METHOD_SUCCESS:
            return updateObject(state, {
                setDefaultPaymentMethodsStatus: 'SUCCESS',
            });
        case actionTypes.SET_DEFAULT_PAYMENT_METHOD_ERROR:
            return updateObject(state, {
                setDefaultPaymentMethodsStatus: 'ERROR',
            });
        case actionTypes.REMOVE_PAYMENT_METHOD_PENDING:
            return updateObject(state, {
                removePaymentMethodRequestStatus: 'LOADING',
            });
        case actionTypes.REMOVE_PAYMENT_METHOD_SUCCESS:
            return updateObject(state, {
                removePaymentMethodRequestStatus: 'SUCCESS',
            });
        case actionTypes.REMOVE_PAYMENT_METHOD_ERROR:
            return updateObject(state, {
                removePaymentMethodRequestStatus: 'ERROR',
            });
        case actionTypes.GET_ADDRESSES_PENDING:
            return updateObject(state, {
                getAddressesListRequestStatus: 'LOADING',
            });
        case actionTypes.GET_ADDRESSES_SUCCESS:
            return updateObject(state, {
                getAddressesListRequestStatus: 'SUCCESS',
                addressesList: action.addressesList,
            });
        case actionTypes.GET_ADDRESSES_ERROR:
            return updateObject(state, {
                getAddressesListRequestStatus: 'ERROR',
            });
        case actionTypes.GET_MY_SAVED_ITEMS_PENDING:
            return updateObject(state, {
                getMySavedItemsRequestStatus: 'LOADING',
            });
        case actionTypes.GET_MY_SAVED_ITEMS_SUCCESS:
            return updateObject(state, {
                getMySavedItemsRequestStatus: 'SUCCESS',
                mySavedItems: action.mySavedItems,
            });
        case actionTypes.GET_MY_SAVED_ITEMS_ERROR:
            return updateObject(state, {
                getMySavedItemsRequestStatus: 'ERROR',
            });
        case actionTypes.GET_USER_SUBSCRIPTION_EMAIL:
            return updateObject(state, {
                userSubscriptionEmail: action.userSubscriptionEmail,
            });
        case actionTypes.GET_USER_SUBSCRIPTION_PHONE:
            return updateObject(state, {
                userSubscriptionPhone: action.userSubscriptionPhone,
            });
        case actionTypes.GET_USER_SUBSCRIPTION_EMAIL_CHANGED:
            return updateObject(state, {
                userSubscriptionEmailChanged: action.userSubscriptionEmailChanged,
            });
        case actionTypes.GET_USER_SUBSCRIPTION_PHONE_CHANGED:
            return updateObject(state, {
                userSubscriptionPhoneChanged: action.userSubscriptionPhoneChanged,
            });
        case actionTypes.GET_USER_SUBSCRIPTION_CORRECT: 
            return updateObject(state, {
                userSubscriptionCorrect: action.userSubscriptionCorrect,
            });
        default:
            return state;
    }
};

export default myProfileReducer;
